import { Icon } from "@iconify/react";
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import {
  addAttribute,
  updateAttribute,
} from "../../reduxStore/slices/attributeSlice";
import { useNotify } from "../../utils/snackbarUtils";

export default function AttributeValue() {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: isLoading, list } = useSelector((state) => state.attribute);
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;
  const { uid } = useParams();

  const [id, setId] = useState("");
  const [attributeName, setAttributeName] = useState("");
  const [desc, setDesc] = useState("");
  const [attrValues, setAttrValues] = useState([]);
  const [recordStatus, setRecordStatus] = useState("");

  useEffect(() => {
    if (uid && list?.length) {
      const attr = list.find((a) => Number(a.uid) === Number(uid));

      if (attr) {
        setId(attr.id || "");
        setAttributeName(attr.name || "");
        setDesc(attr.description || "");
        setAttrValues(attr.attributeValues || []);
        setRecordStatus(attr.record_status || "");
      } else {
        goToListing();
      }
    }
  }, [uid, list]);

  const goToListing = () => {
    navigate("/app/config/attributes-values");
  };

  const handleSaveAttribute = async () => {
    if (attributeName === "") {
      showError("Please enter attribute name.");
      return;
    }
    try {
      const obj = {
        store_id: storeId,
        name: attributeName,
        description: desc,
        attributeValues: attrValues,
      };
      const res = await dispatch(addAttribute(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleUpdateAttribute = async () => {
    if (attributeName === "") {
      showError("Please enter attribute name.");
      return;
    }
    try {
      const obj = {
        store_id: storeId,
        id: id,
        uid: Number(uid),
        name: attributeName,
        description: desc,
        attributeValues: attrValues,
        record_status: recordStatus,
      };
      const res = await dispatch(updateAttribute(obj)).unwrap();
      showSuccess(res.message);
      goToListing();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleAddValue = () => {
    setAttrValues((prev) => [
      {
        value: "",
        is_new_record: true,
        record_status: "A",
      },
      ...prev,
    ]);
  };

  const handleAttrValueChange = (index, newValue) => {
    setAttrValues((prev) =>
      prev.map((val, i) => (i === index ? { ...val, value: newValue } : val))
    );
  };

  const handleDelete = (index) => {
    setAttrValues((prev) => {
      const updated = [...prev];
      const target = updated[index];

      if (target.is_new_record) {
        // new one → remove completely
        return updated.filter((_, i) => i !== index);
      } else {
        // existing → mark deleted
        updated[index] = { ...target, record_status: "R" };
        return updated;
      }
    });
  };

  return (
    <Box p={3}>
      <Container>
        <Box sx={{ maxWidth: "700px", m: "0 auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Back to Attributes & Values">
                <IconButton
                  onClick={() => navigate("/app/config/attributes-values")}
                >
                  <Icon icon="eva:arrow-back-fill" />
                </IconButton>
              </Tooltip>
              <Typography variant="h5">
                {uid ? "Edit Attribute" : "Add Attribute"}
              </Typography>
            </Stack>
          </Stack>

          <Card sx={{ p: "30px", maxWidth: "700px", m: "0 auto" }}>
            <Stack spacing={3}>
              {/* Attribute Info */}
              <TextField
                autoFocus
                autoComplete="attribute_name"
                label="Attribute Name"
                variant="outlined"
                fullWidth
                value={attributeName}
                onChange={(e) => setAttributeName(e.target.value)}
              />

              <TextField
                autoComplete="attribute_desc"
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />

              {/* Attribute Values */}
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Attribute Values</Typography>
                  <Button variant="text" onClick={handleAddValue}>
                    + Add Value
                  </Button>
                </Box>

                {/* <Stack spacing={2}>
                  {attrValues
                    .filter((val) => val.record_status !== "D")
                    .map((val, index) => (
                      <Box
                        key={val.uid || val.id || index}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <TextField
                          label={`Value Name ${index + 1}`}
                          value={val.value}
                          fullWidth
                          onChange={(e) =>
                            handleAttrValueChange(index, e.target.value)
                          }
                        />
                        <Tooltip title="Delete Value">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(index)}
                            disabled={attrValues.length === 1}
                          >
                            <Icon icon="mdi:delete-outline" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                </Stack> */}
                <Stack spacing={2}>
                  {attrValues.filter((v) => v.record_status !== "R").length >
                  0 ? (
                    attrValues
                      .filter((v) => v.record_status !== "R")
                      .map((val, index) => (
                        <Box
                          key={val.uid || val.id || index}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <TextField
                            autoComplete={`attr_value_${index}`}
                            label={`Value Name ${index + 1}`}
                            value={val.value}
                            fullWidth
                            onChange={(e) =>
                              handleAttrValueChange(index, e.target.value)
                            }
                          />
                          <Tooltip title="Delete Value">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(index)}
                              disabled={attrValues.length === 1}
                            >
                              <Icon icon="mdi:delete-outline" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))
                  ) : (
                    <Box
                      sx={{
                        py: 3,
                        textAlign: "center",
                        color: "text.secondary",
                        border: "1px dashed #ddd",
                        borderRadius: 2,
                      }}
                    >
                      <Icon icon="mdi:tag-outline" width={28} height={28} />
                      <Typography variant="body2" mt={1}>
                        No attribute values added yet.
                      </Typography>
                      <Typography
                        variant="body2"
                        fontStyle="italic"
                        color="text.disabled"
                      >
                        Click “+ Add Value” to create one.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => navigate("/app/config/attributes-values")}>
                  Cancel
                </Button>
                <AppButton
                  disabled={isLoading}
                  isLoading={isLoading}
                  size="medium"
                  variant="contained"
                  onClick={uid ? handleUpdateAttribute : handleSaveAttribute}
                >
                  {uid ? "Update" : "Save"}
                </AppButton>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}
